<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import FieldDefinitionFormModal from './FieldDefinitionFormModal.vue';

const store = useStore();
const { t } = useI18n();

const LOCK_ICON = '🔒';
const DRAG_ICON = '⋮⋮';
const SYSTEM_STATUS_KEY = 'status';

const showForm = ref(false);
const editing = ref(null);

const fields = computed(
  () => store.getters['painelFieldDefinitions/getActiveFieldDefinitions']
);
const orderedFields = ref([]);

const refreshOrdered = () => {
  orderedFields.value = [...fields.value].sort(
    (a, b) => a.position - b.position
  );
};

onMounted(async () => {
  await store.dispatch('painelFieldDefinitions/fetch');
  refreshOrdered();
});

const onReorder = async () => {
  const ids = orderedFields.value.map(f => f.id);
  try {
    await store.dispatch('painelFieldDefinitions/reorder', ids);
  } catch {
    refreshOrdered();
  }
};

const openCreate = () => {
  editing.value = null;
  showForm.value = true;
};

const openEdit = field => {
  editing.value = field;
  showForm.value = true;
};

const onSaved = async () => {
  showForm.value = false;
  await store.dispatch('painelFieldDefinitions/fetch');
  refreshOrdered();
};

const remove = async field => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PAINEL.FIELD_DEFINITIONS.REMOVE_CONFIRM_TITLE')))
    return;
  await store.dispatch('painelFieldDefinitions/remove', field.id);
  refreshOrdered();
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <p class="text-sm text-n-slate-11">
        {{ t('PAINEL.FIELD_DEFINITIONS.PAGE_SUBTITLE') }}
      </p>
      <button
        class="rounded bg-n-brand px-3 py-1.5 text-sm text-white"
        @click="openCreate"
      >
        {{ t('PAINEL.FIELD_DEFINITIONS.ADD_FIELD') }}
      </button>
    </div>

    <div class="rounded border border-n-strong bg-n-surface-1">
      <!-- System (Status) row, locked -->
      <div
        class="flex items-center gap-2 border-b border-n-strong bg-n-surface-2 px-4 py-3"
      >
        <span aria-hidden="true">{{ LOCK_ICON }}</span>
        <div class="flex-1">
          <div class="flex items-center gap-2 text-sm font-medium">
            {{ t('PAINEL.FIELD_DEFINITIONS.SYSTEM_STATUS_LABEL') }}
            <span
              class="rounded bg-n-brand px-1.5 py-0.5 text-[10px] uppercase text-white"
            >
              {{ t('PAINEL.FIELD_DEFINITIONS.SYSTEM_PILL') }}
            </span>
          </div>
          <div class="font-mono text-xs text-n-slate-11">
            {{ SYSTEM_STATUS_KEY }}
          </div>
        </div>
      </div>

      <Draggable
        v-model="orderedFields"
        item-key="id"
        handle=".drag-handle"
        ghost-class="opacity-50"
        @end="onReorder"
      >
        <template #item="{ element: field }">
          <div
            class="flex items-center gap-2 border-b border-n-strong px-4 py-3 last:border-b-0"
          >
            <span
              aria-hidden="true"
              class="drag-handle cursor-grab text-n-slate-10"
              >{{ DRAG_ICON }}</span
            >
            <div class="flex-1">
              <div class="text-sm font-medium">{{ field.label }}</div>
              <div class="font-mono text-xs text-n-slate-11">
                {{ field.key }}
              </div>
            </div>
            <span
              class="rounded-full px-2 py-0.5 text-xs"
              :class="
                field.field_type === 'single'
                  ? 'bg-n-blue-3 text-n-blue-11'
                  : 'bg-n-violet-3 text-n-violet-11'
              "
            >
              {{
                field.field_type === 'single'
                  ? t('PAINEL.FIELD_DEFINITIONS.TYPE_SINGLE')
                  : t('PAINEL.FIELD_DEFINITIONS.TYPE_MULTI')
              }}
            </span>
            <span
              class="text-xs"
              :class="field.required ? 'text-n-ruby-9' : 'text-n-slate-11'"
            >
              {{
                field.required
                  ? t('PAINEL.FIELD_DEFINITIONS.REQUIRED_YES')
                  : t('PAINEL.FIELD_DEFINITIONS.REQUIRED_NO')
              }}
            </span>
            <button
              class="text-xs text-n-slate-11 hover:text-n-slate-12"
              @click="openEdit(field)"
            >
              {{ t('PAINEL.FIELD_DEFINITIONS.EDIT') }}
            </button>
            <button
              class="text-xs text-n-ruby-9 hover:text-n-ruby-10"
              @click="remove(field)"
            >
              {{ t('PAINEL.FIELD_DEFINITIONS.REMOVE') }}
            </button>
          </div>
        </template>
      </Draggable>

      <div
        v-if="orderedFields.length === 0"
        class="px-4 py-6 text-center text-sm text-n-slate-11"
      >
        {{ t('PAINEL.FIELD_DEFINITIONS.EMPTY_STATE_TITLE') }}
      </div>
    </div>

    <FieldDefinitionFormModal
      :visible="showForm"
      :field="editing"
      @close="showForm = false"
      @saved="onSaved"
    />
  </div>
</template>
